import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Formato das rotas de listagem chamadas com `?page=` (ver backend lib/paginacao.ts). */
export interface Pagina<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type Filtros = Record<string, string | number | boolean | null | undefined>;

/** Monta a query string ignorando filtro vazio. */
export function montarQuery(params: Filtros): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/** Valor que só muda depois de `ms` sem alteração — para a busca não disparar a cada tecla. */
export function useDebounce<T>(valor: T, ms = 350): T {
  const [atrasado, setAtrasado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setAtrasado(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return atrasado;
}

interface Opcoes {
  pageSize?: number;
  /** false = não busca (ex.: esperando o usuário logar). */
  habilitado?: boolean;
}

/**
 * Lista paginada no servidor. Mudar qualquer filtro volta para a página 1.
 * `recarregar` refaz a página atual (depois de criar/editar/excluir).
 *
 * `R` (opcional) tipa respostas com campos além da página (ex.: totais da folha
 * inteira); a resposta completa fica em `resposta`.
 */
export function usePaginado<T, R extends Pagina<T> = Pagina<T>>(
  caminho: string,
  filtros: Filtros = {},
  opcoes: Opcoes = {},
) {
  const { pageSize = 25, habilitado = true } = opcoes;
  const [page, setPage] = useState(1);
  const [dados, setDados] = useState<R | null>(null);
  const [carregando, setCarregando] = useState(true);

  const chaveFiltros = JSON.stringify(filtros);
  const [chaveAnterior, setChaveAnterior] = useState(chaveFiltros);
  // Filtro novo = página 1 já neste render, para não buscar a página antiga com o filtro novo.
  const filtroMudou = chaveAnterior !== chaveFiltros;
  if (filtroMudou) {
    setChaveAnterior(chaveFiltros);
    setPage(1);
  }
  const paginaEfetiva = filtroMudou ? 1 : page;

  const seq = useRef(0);
  const buscar = useCallback(async () => {
    if (!habilitado) return;
    const minha = ++seq.current;
    setCarregando(true);
    try {
      const qs = montarQuery({ ...JSON.parse(chaveFiltros), page: paginaEfetiva, pageSize });
      const r = await api.get<R>(`${caminho}${qs}`);
      // Resposta atrasada de uma busca anterior não sobrescreve a mais nova.
      if (minha !== seq.current) return;
      // Página além do fim (ex.: excluiu o último item da última página): volta uma.
      if (r.items.length === 0 && r.total > 0 && paginaEfetiva > 1) {
        setPage(Math.max(1, Math.ceil(r.total / pageSize)));
        return;
      }
      setDados(r);
    } catch (err) {
      if (minha === seq.current) toast.error(err instanceof Error ? err.message : "Erro ao carregar a lista");
    } finally {
      if (minha === seq.current) setCarregando(false);
    }
  }, [caminho, chaveFiltros, paginaEfetiva, pageSize, habilitado]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  return {
    items: dados?.items ?? [],
    total: dados?.total ?? 0,
    page: paginaEfetiva,
    pageSize,
    totalPaginas: Math.max(1, Math.ceil((dados?.total ?? 0) / pageSize)),
    carregando,
    setPage,
    recarregar: buscar,
    /** Última resposta completa (null antes da primeira). */
    resposta: dados,
  };
}
